'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  MapPin, Truck, ChevronRight, ChevronLeft, Check,
  Car, Calendar, CreditCard, User, Camera, Clock,
  Droplets, Sparkles, Shield, Star, Wind, Scissors
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const STEPS = [
  { id: 1, label: 'Service Type', icon: MapPin },
  { id: 2, label: 'Package', icon: Droplets },
  { id: 3, label: 'Vehicle Size', icon: Car },
  { id: 4, label: 'Your Info', icon: User },
  { id: 5, label: 'Vehicle Info', icon: Car },
  { id: 6, label: 'Date & Time', icon: Calendar },
  { id: 7, label: 'Payment', icon: CreditCard },
  { id: 8, label: 'Confirm', icon: Check },
];

const SERVICES = [
  { id: 'pkg-001', name: 'Outside Wash Only', price: 30, icon: Droplets, color: 'sky', time: 45 },
  { id: 'pkg-002', name: 'Outside + Inside Wash', price: 50, icon: Car, color: 'blue', popular: true, time: 75 },
  { id: 'pkg-003', name: 'Full Interior Detail', price: 125, icon: Star, color: 'indigo', time: 180 },
  { id: 'pkg-004', name: 'Wax Protection', price: 75, icon: Shield, color: 'emerald', time: 120 },
  { id: 'pkg-005', name: 'Seat Shampoo', price: 75, icon: Scissors, color: 'rose', time: 150 },
  { id: 'pkg-006', name: 'Premium Full Detail', price: 200, icon: Sparkles, color: 'violet', time: 240 },
  { id: 'pkg-007', name: 'Interior Vacuum Only', price: 20, icon: Wind, color: 'purple', time: 30 },
  { id: 'pkg-008', name: 'Window Cleaning', price: 15, icon: Droplets, color: 'cyan', time: 25 },
];

const VEHICLE_SIZES = [
  { id: 'size-small', label: 'Small Car', desc: 'Sedan, Hatchback', icon: '🚗', multiplier: 1 },
  { id: 'size-medium', label: 'Medium Car', desc: 'Coupe, Crossover', icon: '🚙', multiplier: 1.1 },
  { id: 'size-large', label: 'Large / SUV', desc: 'SUV, Pickup Truck', icon: '🛻', multiplier: 1.25 },
  { id: 'size-xl', label: 'XL / Van', desc: 'Van, Company Vehicle', icon: '🚐', multiplier: 1.5 },
];

const TIME_SLOTS = [
  { id: 'ts-0700', time: '07:00', available: true },
  { id: 'ts-0815', time: '08:15', available: true },
  { id: 'ts-0930', time: '09:30', available: false },
  { id: 'ts-1045', time: '10:45', available: true },
  { id: 'ts-1200', time: '12:00', available: true },
  { id: 'ts-1315', time: '13:15', available: false },
  { id: 'ts-1430', time: '14:30', available: true },
  { id: 'ts-1545', time: '15:45', available: true },
  { id: 'ts-1700', time: '17:00', available: true },
];

const PAYMENT_METHODS = [
  { id: 'pay-sentoo', label: 'Sentoo', desc: 'Pay via Sentoo mobile payment', icon: '📱', color: 'blue' },
  { id: 'pay-bank', label: 'Online Banking', desc: 'Transfer via online banking', icon: '🏦', color: 'sky' },
  { id: 'pay-card', label: 'Swipe / Card', desc: 'Card payment on-site at service', icon: '💳', color: 'indigo' },
  { id: 'pay-cash', label: 'Cash', desc: 'Pay cash at time of service', icon: '💵', color: 'emerald' },
];

interface BookingData {
  serviceType: 'location' | 'mobile' | '';
  packageId: string;
  vehicleSize: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  whatsapp: string;
  vehiclePlate: string;
  vehicleBrand: string;
  vehicleModel: string;
  vehicleColor: string;
  address: string;
  date: string;
  timeSlot: string;
  paymentMethod: string;
  notes: string;
}

export default function BookingWizard() {
  const [step, setStep] = useState(1);
  const [bookingData, setBookingData] = useState<BookingData>({
    serviceType: '', packageId: '', vehicleSize: '',
    firstName: '', lastName: '', email: '', phone: '', whatsapp: '',
    vehiclePlate: '', vehicleBrand: '', vehicleModel: '', vehicleColor: '',
    address: '', date: '', timeSlot: '', paymentMethod: '', notes: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, formState: { errors }, trigger } = useForm<BookingData>({
    defaultValues: bookingData,
  });

  const selectedPackage = SERVICES.find((s) => s.id === bookingData.packageId);
  const selectedSize = VEHICLE_SIZES.find((s) => s.id === bookingData.vehicleSize);
  const totalPrice = selectedPackage && selectedSize
    ? Math.round(selectedPackage.price * selectedSize.multiplier)
    : selectedPackage?.price ?? 0;

  const canProceed = () => {
    if (step === 1) return !!bookingData.serviceType;
    if (step === 2) return !!bookingData.packageId;
    if (step === 3) return !!bookingData.vehicleSize;
    if (step === 6) return !!bookingData.date && !!bookingData.timeSlot;
    if (step === 7) return !!bookingData.paymentMethod;
    return true;
  };

  const handleNext = async () => {
    if (step === 4) {
      const valid = await trigger(['firstName', 'lastName', 'email', 'phone']);
      if (!valid) return;
    }
    if (step === 5) {
      const valid = await trigger(['vehiclePlate', 'vehicleBrand', 'vehicleModel']);
      if (!valid) return;
    }
    if (canProceed()) setStep((s) => Math.min(s + 1, 8));
  };

  const handleFinalSubmit = async () => {
    setIsSubmitting(true);
    // Backend integration point: POST /api/appointments with bookingData
    await new Promise((r) => setTimeout(r, 1500));
    setIsSubmitting(false);
    setSubmitted(true);
    toast.success('Appointment booked! You will receive a WhatsApp confirmation shortly.');
  };

  if (submitted) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-gradient-primary flex items-center justify-center mx-auto mb-6 shadow-glow">
            <Check size={36} className="text-white" />
          </div>
          <h2 className="text-2xl font-extrabold text-dark mb-2">Booking Confirmed!</h2>
          <p className="text-muted-foreground mb-2">Appointment #{Math.floor(Math.random() * 9000 + 1000)}</p>
          <p className="text-sm text-muted-foreground mb-6">
            You will receive a WhatsApp confirmation with all details. Our team will contact you to confirm.
          </p>
          <div className="bg-sky-50 rounded-2xl p-5 text-left space-y-2 mb-6">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Service</span><span className="font-semibold text-dark">{selectedPackage?.name}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Date</span><span className="font-semibold text-dark">{bookingData.date}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Time</span><span className="font-semibold text-dark">{bookingData.timeSlot}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Total</span><span className="font-extrabold text-primary">{totalPrice} XCG</span></div>
          </div>
          <a href="/" className="btn-primary w-full justify-center">Back to Home</a>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 py-10">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-extrabold text-dark">Book Your Car Wash</h1>
        <p className="text-muted-foreground mt-2">Complete the steps below to schedule your appointment</p>
      </div>

      {/* Step indicators */}
      <div className="flex items-center justify-center mb-10 overflow-x-auto pb-2">
        <div className="flex items-center gap-1 min-w-max">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = step > s.id;
            const active = step === s.id;
            return (
              <React.Fragment key={`step-ind-${s.id}`}>
                <div className={`flex flex-col items-center gap-1 cursor-pointer ${active ? 'opacity-100' : done ? 'opacity-80' : 'opacity-40'}`}
                  onClick={() => done && setStep(s.id)}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 ${
                    done ? 'bg-gradient-primary text-white shadow-glow' :
                    active ? 'bg-primary text-white ring-4 ring-sky-200': 'bg-sky-100 text-sky-400'
                  }`}>
                    {done ? <Check size={16} /> : <Icon size={16} />}
                  </div>
                  <span className="text-xs font-semibold text-slate-600 hidden md:block whitespace-nowrap">{s.label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`h-0.5 w-8 md:w-12 transition-all duration-300 ${step > s.id ? 'bg-primary' : 'bg-sky-100'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Step content */}
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-3xl shadow-card border border-sky-100 p-6 md:p-8">

          {/* Step 1: Service Type */}
          {step === 1 && (
            <div>
              <h2 className="text-xl font-extrabold text-dark mb-2">Choose Service Type</h2>
              <p className="text-sm text-muted-foreground mb-6">Where would you like your car washed?</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { id: 'location', label: 'Rooi Santu Location', desc: 'Drive in to our professional facility', icon: MapPin, color: 'sky', hours: 'Mon–Sat: 7:00 AM – 6:00 PM' },
                  { id: 'mobile', label: 'Mobile Van', desc: 'We come to your home or office', icon: Truck, color: 'emerald', hours: 'East: 7AM–12PM | West: 12PM–7PM' },
                ].map((option) => {
                  const Icon = option.icon;
                  const active = bookingData.serviceType === option.id;
                  return (
                    <button
                      key={`stype-${option.id}`}
                      onClick={() => setBookingData((d) => ({ ...d, serviceType: option.id as 'location' | 'mobile' }))}
                      className={`p-6 rounded-2xl border-2 text-left transition-all duration-200 ${
                        active
                          ? option.id === 'mobile' ?'border-emerald-500 bg-emerald-50' :'border-primary bg-sky-50' :'border-sky-100 hover:border-sky-200 hover:bg-sky-50/50'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                        option.id === 'mobile' ? 'bg-gradient-to-br from-emerald-500 to-teal-500' : 'bg-gradient-primary'
                      }`}>
                        <Icon size={22} className="text-white" />
                      </div>
                      <h3 className="font-extrabold text-dark mb-1">{option.label}</h3>
                      <p className="text-sm text-muted-foreground mb-2">{option.desc}</p>
                      <p className="text-xs text-sky-600 font-medium">{option.hours}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Package */}
          {step === 2 && (
            <div>
              <h2 className="text-xl font-extrabold text-dark mb-2">Choose Your Service</h2>
              <p className="text-sm text-muted-foreground mb-6">Select the package that fits your needs</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {SERVICES.map((svc) => {
                  const Icon = svc.icon;
                  const active = bookingData.packageId === svc.id;
                  return (
                    <button
                      key={svc.id}
                      onClick={() => setBookingData((d) => ({ ...d, packageId: svc.id }))}
                      className={`p-4 rounded-2xl border-2 text-left transition-all duration-200 relative ${
                        active ? 'border-primary bg-sky-50' : 'border-sky-100 hover:border-sky-200'
                      }`}
                    >
                      {(svc as { popular?: boolean }).popular && (
                        <span className="absolute -top-2 left-3 bg-gradient-primary text-white text-xs font-bold px-2 py-0.5 rounded-full">Popular</span>
                      )}
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0">
                          <Icon size={18} className="text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-dark text-sm">{svc.name}</p>
                          <p className="text-xs text-muted-foreground">{svc.time} min</p>
                        </div>
                        <span className="font-extrabold text-primary tabular-nums text-sm">{svc.price} XCG</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Vehicle Size */}
          {step === 3 && (
            <div>
              <h2 className="text-xl font-extrabold text-dark mb-2">Select Vehicle Size</h2>
              <p className="text-sm text-muted-foreground mb-6">Pricing varies by vehicle size</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {VEHICLE_SIZES.map((size) => {
                  const active = bookingData.vehicleSize === size.id;
                  const price = selectedPackage ? Math.round(selectedPackage.price * size.multiplier) : 0;
                  return (
                    <button
                      key={size.id}
                      onClick={() => setBookingData((d) => ({ ...d, vehicleSize: size.id }))}
                      className={`p-5 rounded-2xl border-2 text-left transition-all duration-200 ${
                        active ? 'border-primary bg-sky-50' : 'border-sky-100 hover:border-sky-200'
                      }`}
                    >
                      <span className="text-3xl mb-3 block">{size.icon}</span>
                      <h3 className="font-extrabold text-dark">{size.label}</h3>
                      <p className="text-sm text-muted-foreground mb-2">{size.desc}</p>
                      {selectedPackage && (
                        <p className="text-primary font-extrabold tabular-nums">{price} XCG</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 4: Customer Info */}
          {step === 4 && (
            <form>
              <h2 className="text-xl font-extrabold text-dark mb-2">Your Information</h2>
              <p className="text-sm text-muted-foreground mb-6">We need your details to confirm the booking</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">First Name *</label>
                  <input
                    {...register('firstName', { required: 'First name is required' })}
                    onChange={(e) => setBookingData((d) => ({ ...d, firstName: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    placeholder="Keisha"
                  />
                  {errors.firstName && <p className="text-xs text-red-500 mt-1">{errors.firstName.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Last Name *</label>
                  <input
                    {...register('lastName', { required: 'Last name is required' })}
                    onChange={(e) => setBookingData((d) => ({ ...d, lastName: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    placeholder="Martina"
                  />
                  {errors.lastName && <p className="text-xs text-red-500 mt-1">{errors.lastName.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Email Address *</label>
                  <input
                    type="email"
                    {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' } })}
                    onChange={(e) => setBookingData((d) => ({ ...d, email: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    placeholder="keisha@email.com"
                  />
                  {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Phone Number *</label>
                  <input
                    {...register('phone', { required: 'Phone number is required' })}
                    onChange={(e) => setBookingData((d) => ({ ...d, phone: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    placeholder="+599 9 000 0000"
                  />
                  {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone.message}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-semibold text-slate-700 mb-1">WhatsApp Number</label>
                  <p className="text-xs text-muted-foreground mb-1.5">Used for booking confirmation messages</p>
                  <input
                    {...register('whatsapp')}
                    onChange={(e) => setBookingData((d) => ({ ...d, whatsapp: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    placeholder="+599 9 000 0000 (if different from phone)"
                  />
                </div>
              </div>
            </form>
          )}

          {/* Step 5: Vehicle Info */}
          {step === 5 && (
            <form>
              <h2 className="text-xl font-extrabold text-dark mb-2">Vehicle Information</h2>
              <p className="text-sm text-muted-foreground mb-6">Tell us about the vehicle we will be washing</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">License Plate *</label>
                  <input
                    {...register('vehiclePlate', { required: 'Plate number is required' })}
                    onChange={(e) => setBookingData((d) => ({ ...d, vehiclePlate: e.target.value.toUpperCase() }))}
                    className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    placeholder="ABC-1234"
                  />
                  {errors.vehiclePlate && <p className="text-xs text-red-500 mt-1">{errors.vehiclePlate.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Vehicle Brand *</label>
                  <input
                    {...register('vehicleBrand', { required: 'Brand is required' })}
                    onChange={(e) => setBookingData((d) => ({ ...d, vehicleBrand: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    placeholder="Toyota"
                  />
                  {errors.vehicleBrand && <p className="text-xs text-red-500 mt-1">{errors.vehicleBrand.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Vehicle Model *</label>
                  <input
                    {...register('vehicleModel', { required: 'Model is required' })}
                    onChange={(e) => setBookingData((d) => ({ ...d, vehicleModel: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    placeholder="Corolla"
                  />
                  {errors.vehicleModel && <p className="text-xs text-red-500 mt-1">{errors.vehicleModel.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Vehicle Color</label>
                  <input
                    {...register('vehicleColor')}
                    onChange={(e) => setBookingData((d) => ({ ...d, vehicleColor: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    placeholder="Silver"
                  />
                </div>
                {bookingData.serviceType === 'mobile' && (
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Service Address *</label>
                    <p className="text-xs text-muted-foreground mb-1.5">Where should the mobile van come?</p>
                    <input
                      {...register('address')}
                      onChange={(e) => setBookingData((d) => ({ ...d, address: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                      placeholder="Kaya Flamboyan 12, Willemstad, Curaçao"
                    />
                  </div>
                )}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Vehicle Photo</label>
                  <p className="text-xs text-muted-foreground mb-1.5">Optional — helps our team identify your vehicle</p>
                  <div className="border-2 border-dashed border-sky-200 rounded-2xl p-6 text-center hover:border-primary transition-colors cursor-pointer">
                    <Camera size={28} className="text-sky-300 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Click to upload or drag and drop</p>
                    <p className="text-xs text-muted-foreground mt-1">JPG, PNG up to 10MB</p>
                  </div>
                </div>
              </div>
            </form>
          )}

          {/* Step 6: Date & Time */}
          {step === 6 && (
            <div>
              <h2 className="text-xl font-extrabold text-dark mb-2">Choose Date &amp; Time</h2>
              <p className="text-sm text-muted-foreground mb-6">Available from tomorrow onwards. Same-day booking is disabled.</p>
              <div className="mb-6">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Select Date</label>
                <input
                  type="date"
                  value={bookingData.date}
                  min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                  onChange={(e) => setBookingData((d) => ({ ...d, date: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">Available Time Slots</label>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {TIME_SLOTS.map((slot) => (
                    <button
                      key={slot.id}
                      disabled={!slot.available}
                      onClick={() => slot.available && setBookingData((d) => ({ ...d, timeSlot: slot.time }))}
                      className={`py-3 rounded-xl text-sm font-semibold transition-all duration-150 ${
                        bookingData.timeSlot === slot.time
                          ? 'bg-gradient-primary text-white shadow-glow'
                          : slot.available
                          ? 'bg-sky-50 text-slate-700 hover:bg-sky-100 border border-sky-100' :'bg-gray-100 text-gray-400 cursor-not-allowed line-through'
                      }`}
                    >
                      {slot.time}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3 flex items-center gap-2">
                  <Clock size={12} />
                  Each appointment reserves 1 hour 15 minutes. Strikethrough = booked.
                </p>
              </div>
            </div>
          )}

          {/* Step 7: Payment */}
          {step === 7 && (
            <div>
              <h2 className="text-xl font-extrabold text-dark mb-2">Payment Method</h2>
              <p className="text-sm text-muted-foreground mb-6">Choose how you would like to pay</p>
              <div className="grid sm:grid-cols-2 gap-3 mb-6">
                {PAYMENT_METHODS.map((pm) => {
                  const active = bookingData.paymentMethod === pm.id;
                  return (
                    <button
                      key={pm.id}
                      onClick={() => setBookingData((d) => ({ ...d, paymentMethod: pm.id }))}
                      className={`p-4 rounded-2xl border-2 text-left transition-all duration-200 ${
                        active ? 'border-primary bg-sky-50' : 'border-sky-100 hover:border-sky-200'
                      }`}
                    >
                      <span className="text-2xl block mb-2">{pm.icon}</span>
                      <p className="font-bold text-dark text-sm">{pm.label}</p>
                      <p className="text-xs text-muted-foreground">{pm.desc}</p>
                    </button>
                  );
                })}
              </div>
              {bookingData.paymentMethod === 'pay-sentoo' && (
                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
                  <p className="text-sm font-semibold text-blue-800 mb-1">Sentoo Payment Instructions</p>
                  <p className="text-xs text-blue-700">Payment details will be sent via WhatsApp after booking confirmation. Sentoo API integration coming soon.</p>
                </div>
              )}
              <div className="mt-4">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Special Notes or Instructions</label>
                <textarea
                  value={bookingData.notes}
                  onChange={(e) => setBookingData((d) => ({ ...d, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
                  placeholder="Any special instructions for our team..."
                />
              </div>
            </div>
          )}

          {/* Step 8: Confirm */}
          {step === 8 && (
            <div>
              <h2 className="text-xl font-extrabold text-dark mb-2">Confirm Your Booking</h2>
              <p className="text-sm text-muted-foreground mb-6">Review your appointment details before confirming</p>
              <div className="space-y-3">
                {[
                  { label: 'Service Type', value: bookingData.serviceType === 'mobile' ? '🚐 Mobile Van' : '📍 Rooi Santu Location' },
                  { label: 'Service Package', value: selectedPackage?.name ?? '—' },
                  { label: 'Vehicle Size', value: selectedSize?.label ?? '—' },
                  { label: 'Customer', value: `${bookingData.firstName} ${bookingData.lastName}` || '—' },
                  { label: 'Vehicle', value: bookingData.vehicleBrand && bookingData.vehicleModel ? `${bookingData.vehicleBrand} ${bookingData.vehicleModel} · ${bookingData.vehiclePlate}` : '—' },
                  { label: 'Date & Time', value: bookingData.date && bookingData.timeSlot ? `${bookingData.date} at ${bookingData.timeSlot}` : '—' },
                  { label: 'Payment', value: PAYMENT_METHODS.find((p) => p.id === bookingData.paymentMethod)?.label ?? '—' },
                ].map((row) => (
                  <div key={`confirm-${row.label}`} className="flex justify-between items-center py-3 border-b border-sky-50">
                    <span className="text-sm text-muted-foreground">{row.label}</span>
                    <span className="text-sm font-semibold text-dark text-right max-w-xs">{row.value}</span>
                  </div>
                ))}
                <div className="flex justify-between items-center py-4 bg-sky-50 rounded-2xl px-4 mt-2">
                  <span className="font-bold text-dark">Total Price</span>
                  <span className="text-2xl font-extrabold text-primary tabular-nums">{totalPrice} XCG</span>
                </div>
              </div>
              {bookingData.notes && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-100 rounded-xl">
                  <p className="text-xs font-semibold text-amber-700 mb-1">Your Notes</p>
                  <p className="text-sm text-amber-800">{bookingData.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-sky-100">
            <button
              onClick={() => setStep((s) => Math.max(s - 1, 1))}
              disabled={step === 1}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm text-slate-600 hover:bg-sky-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              <ChevronLeft size={16} />
              Back
            </button>
            <span className="text-xs text-muted-foreground">Step {step} of {STEPS.length}</span>
            {step < 8 ? (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className="btn-primary text-sm px-6 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
              >
                Continue
                <ChevronRight size={16} />
              </button>
            ) : (
              <button
                onClick={handleFinalSubmit}
                disabled={isSubmitting}
                className="btn-primary text-sm px-6 py-2.5 disabled:opacity-70 min-w-[160px] justify-center"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Booking...
                  </span>
                ) : (
                  <>Confirm Booking <Check size={16} /></>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}